// ./src/contexts/ExpenseTagsContext/tagColors.ts

import { getRandomHexColor } from "@/utils/colorHelpers";

/**
 * Curated color palette for expense tags.
 * These 12 colors are chosen to:
 * - Work well with the forest green theme
 * - Be visually distinct from each other
 * - Provide good contrast for text overlays
 */
export const TAG_COLOR_PALETTE = [
  "#3B82F6", // Blue
  "#8B5CF6", // Purple
  "#EC4899", // Pink
  "#EF4444", // Red
  "#F97316", // Orange
  "#F59E0B", // Amber
  "#84CC16", // Lime
  "#10B981", // Emerald
  "#14B8A6", // Teal
  "#06B6D4", // Cyan
  "#6366F1", // Indigo
  "#A855F7", // Violet
] as const;

/**
 * Get a color from the palette by index.
 * Wraps around if index exceeds palette length.
 */
export const getTagColorByIndex = (index: number): string => {
  const safeIndex = Math.abs(index) % TAG_COLOR_PALETTE.length;
  return TAG_COLOR_PALETTE[safeIndex];
};

/**
 * Get the next auto-assigned color based on existing tag count.
 * Uses palette colors first, then generates random colors.
 */
export const getNextTagColor = (existingTagCount: number): string => {
  if (existingTagCount < TAG_COLOR_PALETTE.length) {
    return TAG_COLOR_PALETTE[existingTagCount];
  }
  return getRandomHexColor();
};

/**
 * Check if a color is from the predefined palette.
 */
export const isColorFromPalette = (color: string): boolean => {
  const normalizedColor = color.toUpperCase();
  return TAG_COLOR_PALETTE.some(
    (paletteColor) => paletteColor.toUpperCase() === normalizedColor
  );
};

/**
 * Get the index of a color in the palette, or -1 if not found.
 */
export const getColorPaletteIndex = (color: string): number => {
  const normalizedColor = color.toUpperCase();
  return TAG_COLOR_PALETTE.findIndex(
    (paletteColor) => paletteColor.toUpperCase() === normalizedColor
  );
};
